import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import TLDVLandingHelmet from 'common/helmets/TLDVLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import TLDV1 from 'img/landing/tldv-1.webp';

import TLDVWordmark from 'img/landing/tldv-wordmark.svg';
import 'css/components/www/_LandingCustomersPage.scss';

const TLDVLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed TLDV Case Study');
  }, [trackEvent]);

  return (
    <div className="landingTLDV landingCustomersPage">
      <TLDVLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How tl;dv uses Canny to transform customer feedback into product innovation" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/-4Pyx2_352Y"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Imagine having all your calls and online meetings recorded and transcribed
              automatically. tl;dv does that and much more. It creates notes, highlights key points,
              and integrates with your entire tech stack. This German-based startup made a splash in
              2021, winning hearts with their witty and relatable video sketches on social media.
              Raphael Allstadt, one of the three co-founders at tl;dv, wears many hats, focusing on
              product, marketing, strategy, and fundraising.
            </p>
            <h2 className="sectionTitle">Problem</h2>
            <p>
              Raphael knew the secret to success: listening to customers. That&apos;s why he sought
              out a feedback tool even before tl;dv was born. Back then, his team created a Discord
              channel to talk to customers.
            </p>
            <p className="quote">“It was very high-maintenance, disorganized, and not scalable."</p>
            <p>
              Because of these limitations, Raphael realized they needed a better solution for
              building a product community.
            </p>
            <p>Without a dedicated feedback tool, Raphael would need to:</p>
            <ul>
              <li>Gather emails for every customer</li>
              <li>Send out emails asking for feedback</li>
              <li>Read through every response</li>
              <li>Sort and categorize replies</li>
            </ul>
            <p>
              Even with all this manual setup, feedback wouldn&apos;t be public. So Raphael
              wouldn&apos;t get the community aspect that he always wanted.
            </p>
            <p>
              Enter Canny &mdash; it was scalable, easy to set up, and user-friendly. Raphael used
              Canny before, and immediately thought of it when starting tl;dv. It became one of the
              first tools he added to his tech stack.
            </p>
            <p className="quote">
              “The moment we had tl;dv, we had Canny. We wanted to give people a platform to voice
              their opinions and give us suggestions and then base our product decisions on that.”
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Today, tl;dv features Canny&apos;s widget on every screen in their app. They get every
              Canny notification in a dedicated Slack channel. This way, they&apos;re always in the
              loop on feature requests, bug reports, and new ideas.
            </p>
            <p className="quote">
              "It&apos;s one thing to gather feedback. It&apos;s another to show that you actually
              listen, care, and act on that feedback."
            </p>
            <p>
              Canny is like a trusty sidekick for tl;dv. It not only brings new feature ideas to the
              table but also shines a light on existing issues. For instance, if users request
              features that already exist, it signals that those features need to be easier to find
              and more user-friendly.
            </p>
            <p className="quote">
              "Canny is an excellent source of real-time inspiration. It helps us really move the
              needle in our business."
            </p>
            <p>tl;dv also uses Canny&apos;s roadmaps to organize and prioritize their feedback.</p>
            <p className="quote">
              "Remember &mdash; the roadmap isn&apos;t a voting contest. You need to apply logical
              and critical thinking when building and reviewing it."
            </p>

            <h2 className="sectionTitle">Results</h2>
            <p>Raphael and his team save a few hours every week by using Canny.</p>
            <p className="quote">
              "Customer discovery is so fast and easy with Canny. Without it, we&apos;d completely
              lack visibility into our customers."
            </p>
            <p>
              Canny helps tl;dv stay focused and prevents them from building the wrong features.
              Plus, it helps them build an engaged community around their product.
            </p>
            <p className="quote">
              "Our customers often mentioned how prominently we feature Canny in our product. We
              believe that our users perceive us as open, close to the customer, and receptive to
              feedback because of it."
            </p>

            <LazyLoadedImage className="border" src={TLDV1} width={250} />

            <p>The tl;dv team also wins back customers thanks to Canny.</p>
            <p className="quote">
              "A typical user submits or votes on a feature request and disengages immediately
              &mdash; until they hear back. Canny helps us reengage them. Even simply acknowledging
              their request or clarifying the ask makes a difference."
            </p>
            <p>
              Overall, tl;dv attributes increased engagement throughout the entire customer journey
              to Canny.
            </p>
            <p className="quote">
              "Sometimes we release a new feature and only announce it in Canny. This gives our
              engaged users an exclusive first look at our new features. If they voted for or
              submitted that request, they get to see it first."
            </p>
            <p>
              Having founded multiple startups, Raphael has always championed the power of feedback.
              And he believes every startup should do the same.
            </p>
            <p className="quote">
              "Feedback is so important, especially in the early days. Do everything you can to get
              it, and make feedback submission as easy as possible. Then, radiate it to your entire
              team."
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage alt="TLDV Company" className="companyLogo" src={TLDVWordmark} />
            tl;dv (“too long, didn&apos;t view”) is an AI meeting assistant that goes beyond simply
            recording, transcribing, and summarizing your team&apos;s meetings. tl;dv provides
            insights from meetings across various teams and individuals within your organization. It
            seamlessly integrates with CRMs, ticketing systems, knowledge management platforms, and
            over 5000 other tools.
            <a href="https://tldv.io/" rel="noopener" target="_blank">
              tldv.io
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>20</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2021</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2021</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>2,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default hot(TLDVLanding);
